import type { ReactNode } from "react";
import { useMemo } from "react";
import "./ScheduleTemplatesList.scss";
import ScheduleTemplateCell from "@/pages/human-resources/time-management/components/schedule-templates/ScheduleTemplateCell.tsx";
import classNames from "classnames";
import { normalizedCharacters } from "@/shared/utils/Strings.ts";
import CreateTemplateButton from "@/pages/human-resources/time-management/components/schedule-templates/CreateTemplateButton.tsx";
import { keyFnToComparator, sequenced } from "@/shared/utils/Comparator.ts";
import type { ScheduleTemplate } from "@/pages/human-resources/time-management/business/schedule-template/ScheduleTemplate.ts";
import CornerIconsLegend from "@/pages/human-resources/time-management/components/schedule-palette/bottom-row/legend/CornerIconsLegend.tsx";
import useKeywordsFilter from "@/shared/custom-hook/useKeywordsFilter.ts";
import useScheduleUserPermissions from "@/pages/human-resources/time-management/hooks/useScheduleUserPermissions.ts";
import BasicTooltipWithOffset from "@/pages/human-resources/time-management/components/BasicTooltipWithOffset.tsx";
import { useTranslation } from "react-i18next";
import HelpMessage from "@/pages/human-resources/time-management/components/daily-tags/HelpMessage.tsx";
import { findTemplatesDuplicateIdsAndScopes } from "@/pages/human-resources/time-management/components/schedule-palette/bottom-row/legend/EntityScope.ts";

interface Properties {
  scheduleTemplates: ScheduleTemplate[];
  selectedTemplate: ScheduleTemplate | undefined;
  onSelectTemplate: (st: ScheduleTemplate) => void;
  horizontal: boolean;
  searchInput: string;
  helpText?: string;
}

export default function ScheduleTemplatesList(props: Properties): ReactNode {
  const { t } = useTranslation(["humanResources"], {
    keyPrefix: "timeManagement",
  });
  const { canEditSiteTemplates } = useScheduleUserPermissions();
  const searchFilter = useKeywordsFilter(
    props.searchInput,
    props.scheduleTemplates,
    (st) => st.label,
  );

  const duplicates = useMemo(() => {
    return findTemplatesDuplicateIdsAndScopes(props.scheduleTemplates);
  }, [props.scheduleTemplates]);

  const sortedTemplates = useMemo(() => {
    const res = [...props.scheduleTemplates];
    res.sort(
      sequenced<ScheduleTemplate>([
        keyFnToComparator((st) => normalizedCharacters(st.label)),
        keyFnToComparator(
          (st) => st.timeSlots.at(0)?.startTime.toMillis() ?? 0,
        ),
      ]),
    );
    return res;
  }, [props.scheduleTemplates]);

  return (
    <div
      className={classNames(
        "schedule-templates-list",
        props.horizontal && "horizontal",
      )}
    >
      {props.helpText !== undefined && <HelpMessage text={props.helpText} />}
      <div className="scrollable-container">
        <div className="scrollable-list">
          {canEditSiteTemplates && (
            <BasicTooltipWithOffset title={t("scheduleTemplateNew")}>
              <CreateTemplateButton />
            </BasicTooltipWithOffset>
          )}
          {sortedTemplates.filter(searchFilter).map((st) => (
            <ScheduleTemplateCell
              key={st.id}
              template={st}
              isDuplicate={duplicates.entities.has(st)}
              selected={props.selectedTemplate === st}
              onClick={() => {
                props.onSelectTemplate(st);
              }}
            />
          ))}
        </div>
      </div>
      <CornerIconsLegend scopes={duplicates.scopes} />
    </div>
  );
}
